import { Badge, EmptyState, PageHeader, Select, Skeleton } from '@/components';
import { useReleaseProducts } from '@/features/release-config/hooks';
import { ChangelogView } from '@/features/releases/ChangelogView';
import { useCompareReleases, useRelease, useReleaseVersions } from '@/features/releases/hooks';
import {
  RELEASE_ENVIRONMENTS,
  type ReleaseEnvironment,
  type ReleaseVersionGroup,
} from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

/** One side of the comparison: version + build (representative preselected). */
const SidePicker = (props: {
  label: string;
  groups: ReleaseVersionGroup[];
  version: string;
  buildId: string;
  onVersion: (v: string) => void;
  onBuild: (id: string) => void;
}) => {
  const group = () => props.groups.find((g) => g.version === props.version);
  return (
    <div class="flex items-end gap-2">
      <div class="min-w-36">
        <Select
          label={props.label}
          value={props.version}
          onChange={props.onVersion}
          placeholder="Version"
          options={props.groups.map((g) => ({ value: g.version, label: g.version }))}
        />
      </div>
      <Show when={group()}>
        {(g) => (
          <div class="min-w-36">
            <Select
              label="Build"
              value={props.buildId || g().representativeReleaseId}
              onChange={props.onBuild}
              options={g().builds.map((b) => ({
                value: b.id,
                label: `#${b.buildNumber} (${b.status})`,
              }))}
            />
          </div>
        )}
      </Show>
    </div>
  );
};

export default function ReleaseVersionsCompare() {
  const products = useReleaseProducts();
  const [productId, setProductId] = createSignal('');
  const [environment, setEnvironment] = createSignal<ReleaseEnvironment>('dev');
  const [baseVersion, setBaseVersion] = createSignal('');
  const [baseBuild, setBaseBuild] = createSignal('');
  const [headVersion, setHeadVersion] = createSignal('');
  const [headBuild, setHeadBuild] = createSignal('');

  const versions = useReleaseVersions(() =>
    productId() ? { productId: productId(), environment: environment() } : null,
  );
  const groups = () => versions.data?.data ?? [];

  const resolveBuild = (version: string, override: string) =>
    override || (groups().find((g) => g.version === version)?.representativeReleaseId ?? '');

  const baseId = () => (baseVersion() ? resolveBuild(baseVersion(), baseBuild()) : '');
  const headId = () => (headVersion() ? resolveBuild(headVersion(), headBuild()) : '');

  const compare = useCompareReleases(() =>
    baseId() && headId() ? { baseReleaseId: baseId(), headReleaseId: headId() } : null,
  );
  const headDetail = useRelease(() => headId());

  const resetVersions = () => {
    setBaseVersion('');
    setBaseBuild('');
    setHeadVersion('');
    setHeadBuild('');
  };

  return (
    <section>
      <PageHeader
        title="Compare versions"
        sub="Change log between two versions of a product and environment."
      />

      <div class="mx-auto max-w-6xl space-y-4 p-6">
        <div class="flex flex-wrap items-end gap-2">
          <div class="min-w-44">
            <Select
              label="Product"
              value={productId()}
              onChange={(v) => {
                setProductId(v);
                resetVersions();
              }}
              placeholder="Select product"
              options={(products.data?.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          </div>
          <div class="min-w-32">
            <Select
              label="Environment"
              value={environment()}
              onChange={(v) => {
                setEnvironment(v as ReleaseEnvironment);
                resetVersions();
              }}
              options={RELEASE_ENVIRONMENTS.map((e) => ({ value: e, label: e.toUpperCase() }))}
            />
          </div>
        </div>

        <Show when={productId()}>
          <div class="flex flex-wrap items-end gap-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
            <SidePicker
              label="From version"
              groups={groups()}
              version={baseVersion()}
              buildId={baseBuild()}
              onVersion={(v) => {
                setBaseVersion(v);
                setBaseBuild('');
              }}
              onBuild={setBaseBuild}
            />
            <SidePicker
              label="To version"
              groups={groups()}
              version={headVersion()}
              buildId={headBuild()}
              onVersion={(v) => {
                setHeadVersion(v);
                setHeadBuild('');
              }}
              onBuild={setHeadBuild}
            />
          </div>
        </Show>

        <Show when={baseId() && headId()}>
          <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
            <Show when={(headDetail.data?.data.requirements ?? []).length > 0}>
              <div class="mb-3 flex flex-wrap items-center gap-2 text-sm">
                <span class="text-neutral-500">Requires:</span>
                <For each={headDetail.data?.data.requirements ?? []}>
                  {(req) => (
                    <Badge tone="neutral">
                      {req.productName} {req.version}
                    </Badge>
                  )}
                </For>
              </div>
            </Show>
            <Show when={!compare.isPending} fallback={<Skeleton class="h-24" />}>
              <Show
                when={compare.data?.data}
                fallback={<EmptyState variant="error" title="Failed to compare" />}
              >
                {(d) => <ChangelogView changelog={d()} />}
              </Show>
            </Show>
          </div>
        </Show>
      </div>
    </section>
  );
}
